import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { darkFinanceTheme } from '../theme/darkTheme';
import { lightFinanceTheme } from '../theme/lightTheme';

type ThemeMode = 'dark' | 'light';

const STORAGE_KEY = 'cryptoportfolio_theme';

interface IThemeContext {
    mode: ThemeMode;
    toggleTheme: () => void;
}

const ThemeContext = createContext<IThemeContext>({
    mode: 'dark',
    toggleTheme: () => {},
});

export function useThemeMode() {
    return useContext(ThemeContext);
}

export function AppThemeProvider({ children }: { children: React.ReactNode }) {
    const [mode, setMode] = useState<ThemeMode>(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored === 'light' ? 'light' : 'dark';
    });

    const toggleTheme = useCallback(() => {
        setMode((prev) => {
            const next: ThemeMode = prev === 'dark' ? 'light' : 'dark';
            localStorage.setItem(STORAGE_KEY, next);
            return next;
        });
    }, []);

    const theme = mode === 'dark' ? darkFinanceTheme : lightFinanceTheme;

    const value = useMemo(() => ({ mode, toggleTheme }), [mode, toggleTheme]);

    return (
        <ThemeContext.Provider value={value}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    );
}
